import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, AlertCircle, Shield, Smartphone, Settings } from "lucide-react";
import { getMFAStatus, disableMFA } from "@/services/mfaService";
import MFASetup from "./MFASetup";

const MFAManagement = () => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSetup, setShowSetup] = useState(false);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    loadMFAStatus();
  }, []);

  const loadMFAStatus = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getMFAStatus();
      setMfaStatus(response);
    } catch (error) {
      setError(error.message || "Failed to load MFA status");
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!window.confirm("Are you sure you want to disable MFA? This will make your account less secure.")) {
      return;
    }

    try {
      setDisabling(true);
      setError("");
      await disableMFA();
      setSuccess("MFA has been disabled successfully");
      await loadMFAStatus();
    } catch (error) {
      setError(error.message || "Failed to disable MFA");
    } finally {
      setDisabling(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    setSuccess("MFA setup completed successfully!");
    loadMFAStatus();
  };

  const handleSetupCancel = () => {
    setShowSetup(false);
  };

  if (showSetup) {
    return (
      <MFASetup 
        onComplete={handleSetupComplete}
        onCancel={handleSetupCancel}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Multi-Factor Authentication</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            MFA Status
          </CardTitle>
          <CardDescription>
            Manage your multi-factor authentication settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mfaStatus && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Authenticator App</p>
                    <p className="text-sm text-gray-500">Time-based One-Time Password (TOTP)</p>
                  </div>
                </div>
                <Badge variant={mfaStatus.mfaEnabled ? "default" : "secondary"}>
                  {mfaStatus.mfaEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>

              {mfaStatus.mfaDevices && mfaStatus.mfaDevices.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Registered Devices:</h4>
                  {mfaStatus.mfaDevices.map((device, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{device.DeliveryMedium || "Software Token"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3">
            {!mfaStatus?.mfaEnabled ? (
              <Button onClick={() => setShowSetup(true)} className="flex-1">
                <Settings className="mr-2 h-4 w-4" />
                Enable MFA
              </Button>
            ) : (
              <Button 
                variant="destructive" 
                onClick={handleDisableMFA}
                disabled={disabling}
                className="flex-1"
              >
                {disabling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Disabling...
                  </>
                ) : (
                  "Disable MFA"
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Security Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium">What is MFA?</h4>
            <p className="text-sm text-gray-600">
              Multi-Factor Authentication adds an extra layer of security to your account by requiring 
              a second form of verification in addition to your password.
            </p>
          </div>
          
          <div className="space-y-2">
            <h4 className="font-medium">How it works:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Enter your email and password as usual</li>
              <li>• Open your authenticator app and enter the 6-digit code</li>
              <li>• Complete the sign-in process</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">Recommended Apps:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Google Authenticator</li>
              <li>• Microsoft Authenticator</li>
              <li>• Authy</li>
              <li>• 1Password</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MFAManagement;